import { useState, useEffect } from 'react'
import { Users, Plus, Trash2, FolderOpen, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { apiClient } from '../services/api'

interface ClientAccount {
  id: number
  name: string
  email: string
  company: string | null
  is_active: boolean
  created_at: string
  last_login_at: string | null
}

interface Project {
  id: number
  name: string
  status: string
}

interface CreateClientForm {
  name: string
  email: string
  password: string
  company: string
}

const blank = (): CreateClientForm => ({ name: '', email: '', password: '', company: '' })

export default function ClientPortalPage() {
  const [clients, setClients] = useState<ClientAccount[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(blank())
  const [showPassword, setShowPassword] = useState(false)
  const [formErrors, setFormErrors] = useState<Partial<CreateClientForm>>({})
  const [assigningClient, setAssigningClient] = useState<ClientAccount | null>(null)
  const [selectedProjects, setSelectedProjects] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const [cRes, pRes] = await Promise.all([
        apiClient.get<ClientAccount[]>('/api/v1/client-portal/clients'),
        apiClient.get<Project[]>('/api/v1/projects'),
      ])
      setClients(cRes.data)
      setProjects(pRes.data)
    } catch (e) {
      // Managing client accounts is for workspace admins: say so plainly
      // instead of a generic error.
      if ((e as { response?: { status?: number } })?.response?.status === 403) setForbidden(true)
      else toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const validate = (): boolean => {
    const errs: Partial<CreateClientForm> = {}
    if (!form.name.trim()) errs.name = 'Name is required'
    if (!form.email.trim()) {
      errs.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Enter a valid email address'
    }
    if (!form.password.trim()) {
      errs.password = 'Password is required'
    } else if (form.password.length < 8) {
      errs.password = 'Password must be at least 8 characters'
    }
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleCreate = async () => {
    if (!validate()) return
    setCreating(true)
    try {
      const res = await apiClient.post<ClientAccount>('/api/v1/client-portal/clients', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        company: form.company.trim() || null,
      })
      setClients(prev => [res.data, ...prev])
      setForm(blank())
      setShowCreate(false)
      toast.success('Client account created')
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      if (detail?.includes('Email already')) {
        setFormErrors(f => ({ ...f, email: 'This email is already registered' }))
      } else {
        toast.error(detail || 'Failed to create client')
      }
    } finally {
      setCreating(false)
    }
  }

  const handleDeactivate = async (client: ClientAccount) => {
    if (!confirm(`Deactivate account for ${client.name}? They will no longer be able to log in.`)) return
    try {
      await apiClient.delete(`/api/v1/client-portal/clients/${client.id}`)
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, is_active: false } : c))
      toast.success('Account deactivated')
    } catch {
      toast.error('Failed to deactivate account')
    }
  }

  const openAssign = (client: ClientAccount) => {
    setAssigningClient(client)
    setSelectedProjects([])
  }

  const handleAssign = async () => {
    if (!assigningClient) return
    setSaving(true)
    try {
      await apiClient.patch(`/api/v1/client-portal/clients/${assigningClient.id}/projects`, {
        project_ids: selectedProjects,
      })
      setAssigningClient(null)
      toast.success(`Projects updated for ${assigningClient.name}`)
    } catch {
      toast.error('Failed to assign projects')
    } finally {
      setSaving(false)
    }
  }

  const toggleProject = (id: number) => {
    setSelectedProjects(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    )
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never'
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-[#102a43]">Client Portal</h1>
        <p className="text-sm text-[#627d98] mt-1">
          External clients get a scoped view of specific projects and can comment on milestones.
          Internal data is never visible to clients.
        </p>
      </div>

      {forbidden ? (
        <div role="status" className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground">
          Only workspace admins can manage client accounts. Ask an owner or admin to add a client or share a project.
        </div>
      ) : (
      <div className="bg-white border-2 border-[#bcccdc] rounded-lg shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-[#e2eaf2]">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#486581]" aria-hidden="true" />
            <h2 className="font-semibold text-[#102a43]">Client Accounts</h2>
          </div>
          <button
            onClick={() => setShowCreate(v => !v)}
            className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg transition-colors text-sm font-medium"
            aria-expanded={showCreate}
            aria-controls="create-client-form"
          >
            <Plus size={16} aria-hidden="true" />
            Add Client
          </button>
        </div>

        {showCreate && (
          <div
            id="create-client-form"
            className="p-5 bg-[#f0f4f8] border-b-2 border-[#bcccdc] space-y-4"
            role="region"
            aria-label="Create client account"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label htmlFor="cl-name" className="block text-sm font-medium text-[#334e68] mb-1">
                  Full name <span className="text-red-600" aria-hidden="true">*</span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id="cl-name"
                  type="text"
                  value={form.name}
                  onChange={e => { setForm(f => ({ ...f, name: e.target.value })); if (formErrors.name) setFormErrors(f => ({ ...f, name: undefined })) }}
                  onBlur={() => { if (!form.name.trim()) setFormErrors(f => ({ ...f, name: 'Name is required' })) }}
                  placeholder="Jane Smith"
                  className={`w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.name ? 'border-red-400' : 'border-[#bcccdc]'}`}
                  aria-describedby={formErrors.name ? 'cl-name-error' : undefined}
                  aria-required="true"
                  aria-invalid={!!formErrors.name}
                />
                {formErrors.name && (
                  <p id="cl-name-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                    <AlertCircle size={12} aria-hidden="true" />{formErrors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="cl-email" className="block text-sm font-medium text-[#334e68] mb-1">
                  Email <span className="text-red-600" aria-hidden="true">*</span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id="cl-email"
                  type="email"
                  value={form.email}
                  onChange={e => { setForm(f => ({ ...f, email: e.target.value })); if (formErrors.email) setFormErrors(f => ({ ...f, email: undefined })) }}
                  onBlur={() => {
                    if (!form.email.trim()) setFormErrors(f => ({ ...f, email: 'Email is required' }))
                    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) setFormErrors(f => ({ ...f, email: 'Enter a valid email' }))
                  }}
                  placeholder="jane@acmecorp.com"
                  autoComplete="off"
                  className={`w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.email ? 'border-red-400' : 'border-[#bcccdc]'}`}
                  aria-describedby={formErrors.email ? 'cl-email-error' : undefined}
                  aria-required="true"
                  aria-invalid={!!formErrors.email}
                />
                {formErrors.email && (
                  <p id="cl-email-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                    <AlertCircle size={12} aria-hidden="true" />{formErrors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="cl-pass" className="block text-sm font-medium text-[#334e68] mb-1">
                  Password <span className="text-red-600" aria-hidden="true">*</span>
                  <span className="sr-only">(required, minimum 8 characters)</span>
                </label>
                <div className="relative">
                  <input
                    id="cl-pass"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => { setForm(f => ({ ...f, password: e.target.value })); if (formErrors.password) setFormErrors(f => ({ ...f, password: undefined })) }}
                    onBlur={() => {
                      if (!form.password) setFormErrors(f => ({ ...f, password: 'Password is required' }))
                      else if (form.password.length < 8) setFormErrors(f => ({ ...f, password: 'Minimum 8 characters' }))
                    }}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    className={`w-full border-2 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.password ? 'border-red-400' : 'border-[#bcccdc]'}`}
                    aria-describedby={formErrors.password ? 'cl-pass-error' : 'cl-pass-hint'}
                    aria-required="true"
                    aria-invalid={!!formErrors.password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#829ab1] hover:text-[#486581]"
                  >
                    {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                  </button>
                </div>
                <p id="cl-pass-hint" className="text-[#829ab1] text-xs mt-1">Share this password securely with the client.</p>
                {formErrors.password && (
                  <p id="cl-pass-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                    <AlertCircle size={12} aria-hidden="true" />{formErrors.password}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <label htmlFor="cl-company" className="block text-sm font-medium text-[#334e68] mb-1">
                  Company <span className="text-[#829ab1] text-xs">(optional)</span>
                </label>
                <input
                  id="cl-company"
                  type="text"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Acme Corp"
                  className="w-full border-2 border-[#bcccdc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581]"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCreate(false); setForm(blank()); setFormErrors({}) }}
                className="px-4 py-2.5 min-h-[44px] border-2 border-[#bcccdc] rounded-lg text-sm text-[#334e68] hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create Account'}
              </button>
            </div>
          </div>
        )}

        {/* Client list */}
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-[#829ab1] py-4 text-center" role="status">Loading clients…</p>
          ) : clients.length === 0 ? (
            <div className="text-center py-10 text-[#829ab1]">
              <Users size={32} className="mx-auto mb-2 opacity-40" aria-hidden="true" />
              <p className="text-sm">No client accounts yet.</p>
            </div>
          ) : (
            <ul className="space-y-3" aria-label="Client accounts">
              {clients.map(c => (
                <li
                  key={c.id}
                  className="flex items-center gap-3 p-4 border-2 border-[#bcccdc] rounded-lg bg-white"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-[#102a43]">{c.name}</span>
                      {c.company && <span className="text-xs text-[#627d98]">· {c.company}</span>}
                      <span className={`px-2 py-0.5 rounded text-xs border font-medium ${c.is_active ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-xs text-[#829ab1] mt-0.5 flex flex-wrap gap-x-4">
                      <span>{c.email}</span>
                      <span>Last login: {formatDate(c.last_login_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openAssign(c)}
                      aria-label={`Assign projects to ${c.name}`}
                      className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#f0f4f8] transition-colors text-[#486581]"
                      title="Assign projects"
                    >
                      <FolderOpen size={16} aria-hidden="true" />
                    </button>
                    {c.is_active && (
                      <button
                        onClick={() => handleDeactivate(c)}
                        aria-label={`Deactivate ${c.name}`}
                        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      )}

      {/* Assign projects modal */}
      {assigningClient && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label={`Assign projects to ${assigningClient.name}`}
        >
          <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-xl w-full max-w-lg mx-4">
            <div className="p-5 border-b border-[#e2eaf2]">
              <h2 className="font-semibold text-[#102a43]">Assign Projects — {assigningClient.name}</h2>
              <p className="text-sm text-[#627d98] mt-0.5">
                Only these projects will appear in the client's portal. Internal data remains hidden.
              </p>
            </div>

            <div className="p-5 max-h-80 overflow-y-auto">
              {projects.length === 0 ? (
                <p className="text-sm text-[#829ab1]">No projects available.</p>
              ) : (
                <ul className="space-y-2" role="group" aria-label="Available projects">
                  {projects.map(p => (
                    <li key={p.id}>
                      <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors min-h-[44px] hover:border-[#829ab1] border-[#bcccdc]">
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                          className="w-4 h-4 accent-[#486581]"
                        />
                        <div>
                          <span className="text-sm font-medium text-[#102a43]">{p.name}</span>
                          <span className="ml-2 text-xs text-[#829ab1]">{p.status}</span>
                        </div>
                        {selectedProjects.includes(p.id) && (
                          <CheckCircle size={16} className="ml-auto text-[#486581]" aria-hidden="true" />
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="p-5 border-t border-[#e2eaf2] flex gap-3 justify-end">
              <button
                onClick={() => setAssigningClient(null)}
                className="px-4 py-2.5 min-h-[44px] border-2 border-[#bcccdc] rounded-lg text-sm text-[#334e68] hover:bg-[#f0f4f8] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={saving}
                className="px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : `Grant access to ${selectedProjects.length} project${selectedProjects.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
